import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { Link } from "react-router-dom";
import { NAV_LINKS } from "../../data/content";
import { Logo } from "./Logo";
import { useLang } from "../../i18n/LanguageContext";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";

const LangToggle = ({ scrolled, suffix = "" }) => {
  const { lang, setLang } = useLang();
  const base = scrolled ? "text-[#4A4D48]" : "text-[#F6F5F0]/70";
  return (
    <div data-testid={`lang-toggle${suffix}`} className={`flex items-center text-sm font-semibold ${scrolled ? "" : "bg-black/20 rounded-full px-1 py-0.5"}`}>
      {["en", "fr"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i === 1 && <span className={`mx-0.5 ${base}`}>·</span>}
          <button
            data-testid={`lang-${l}${suffix}`}
            onClick={() => setLang(l)}
            className={`px-2 py-0.5 rounded-full transition-colors ${
              lang === l ? (scrolled ? "text-[#1F5A34]" : "text-white") : `${base} hover:opacity-80`
            }`}
          >
            {l.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const { count, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#F6F5F0]/85 backdrop-blur-xl border-b border-[#D1CFC8]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="group inline-flex items-center">
          <Logo variant={scrolled ? "color" : "white"} />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className={`text-sm font-medium relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 hover:after:w-full after:bg-current after:transition-all after:duration-300 ${
                scrolled ? "text-[#4A4D48] hover:text-[#1F5A34]" : "text-[#F6F5F0]/90 hover:text-white"
              }`}
            >
              {t.nav[l.id]}
            </a>
          ))}
          <LangToggle scrolled={scrolled} />
          <Link to="/shop" data-testid="nav-shop-link" className={`text-sm font-semibold ${scrolled ? "text-[#1F5A34]" : "text-white"}`}>{t.nav.shop}</Link>
          <Link to={user ? "/account" : "/login"} data-testid="nav-account-link" className={scrolled ? "text-[#4A4D48] hover:text-[#1F5A34]" : "text-[#F6F5F0]/90 hover:text-white"}>
            <User className="w-5 h-5" />
          </Link>
          <button onClick={() => setCartOpen(true)} data-testid="nav-cart-btn" className={`relative ${scrolled ? "text-[#1F5A34]" : "text-white"}`}>
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#69A544] text-white text-[10px] flex items-center justify-center">{count}</span>}
          </button>
          <a
            href="#contact"
            data-testid="nav-cta"
            className="px-5 py-2.5 rounded-full bg-[#1F5A34] text-[#F6F5F0] text-sm font-semibold hover:bg-[#163F25] transition-colors"
          >
            {t.nav.requestPricing}
          </a>
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <button onClick={() => setCartOpen(true)} data-testid="nav-cart-btn-m" className={`relative ${scrolled ? "text-[#1F5A34]" : "text-white"}`}>
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#69A544] text-white text-[10px] flex items-center justify-center">{count}</span>}
          </button>
          <LangToggle scrolled={scrolled} suffix="-m" />
          <button
            data-testid="mobile-menu-toggle"
            className={scrolled ? "text-[#1A1C19]" : "text-[#F6F5F0]"}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden bg-[#F6F5F0] border-t border-[#D1CFC8] px-6 py-4 flex flex-col gap-4"
        >
          {NAV_LINKS.map((l) => (
            <a key={l.id} href={`#${l.id}`} data-testid={`mobile-nav-${l.id}`} onClick={() => setOpen(false)} className="text-[#1A1C19] font-medium">
              {t.nav[l.id]}
            </a>
          ))}
          <a href="#contact" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full bg-[#1F5A34] text-[#F6F5F0] text-sm font-semibold text-center">
            {t.nav.requestPricing}
          </a>
          <div className="flex gap-4 pt-2 border-t border-[#D1CFC8]">
            <Link to="/shop" onClick={() => setOpen(false)} className="text-[#1F5A34] font-semibold">{t.nav.shop}</Link>
            <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} className="text-[#1F5A34] font-semibold">{t.nav.account}</Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
